import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { LockClosed12Regular } from '@fluentui/react-icons';
import { useState, type DragEvent } from 'react';
import type { PolicyDiff, PolicyRule, PolicyStatus } from '../../api/types';
import { StatePill } from '../../components/StatePill';
import { vt } from '../../theme';
import { DEFAULT_RULE_ID, formatEndpoint, formatPorts, formatProtocol, lastMatchTitle, ruleHits, rowChange, type RuleHits } from './model';

const WIDTHS = ['26px', '96px', '64px', '136px', '150px', '80px', 'auto', '72px'];
// The default row lines up with the table's columns.
const COLUMNS = '26px 96px 64px 136px 150px 80px minmax(0, 1fr) 72px';

const useStyles = makeStyles({
  table: { tableLayout: 'fixed', width: '100%' },
  head: {
    height: '30px',
    fontSize: '11.5px',
    color: vt.fg3,
    fontWeight: tokens.fontWeightRegular,
    borderBottom: `1px solid ${vt.surface2}`,
  },
  row: {
    height: '34px',
    borderBottom: `1px solid ${vt.surface2}`,
    cursor: 'pointer',
    ':focus-visible': { outline: `2px solid ${vt.brand}`, outlineOffset: '-2px' },
  },
  // An edited row: a warm wash and a warm left edge (mockup 05), so the draft's shape shows at a glance.
  edited: {
    backgroundColor: vt.tintOrange,
    boxShadow: `inset 3px 0 0 ${vt.orange}`,
    ':hover': { backgroundColor: vt.tintOrange },
  },
  selected: { backgroundColor: vt.brandTint, ':hover': { backgroundColor: vt.brandTint } },
  selectedEdited: { boxShadow: `inset 3px 0 0 ${vt.orange}` },
  dropTarget: { boxShadow: `inset 0 2px 0 ${vt.brand}` },
  dim: { color: vt.fg4, '& *': { color: vt.fg4 } },
  cell: { paddingBlock: 0, boxSizing: 'border-box', overflow: 'hidden', whiteSpace: 'nowrap' },
  end: { textAlign: 'end', '& > *': { justifyContent: 'flex-end' } },
  // Mockup 05: the count, with the last match stacked under it in small muted mono.
  hitStack: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', rowGap: '1px' },
  hits: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11.5px', lineHeight: '14px', color: vt.fg1 },
  ago: { fontFamily: tokens.fontFamilyMonospace, fontSize: '9.5px', lineHeight: '11px', color: vt.fg4 },
  num: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11.5px', color: vt.fg4 },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', color: vt.fg1 },
  comment: { fontSize: '11px', color: vt.fg3, overflow: 'hidden', textOverflow: 'ellipsis' },
  policy: { display: 'inline-flex', alignItems: 'center', columnGap: '6px' },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '18px',
    maxWidth: '100%',
    boxSizing: 'border-box',
    paddingInline: '8px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: vt.tintNeutral,
    color: vt.fg2,
    fontSize: '10.5px',
    lineHeight: '18px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  chipMono: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px' },
  off: { color: vt.fg4 },
  // The pinned default: dashed, dimmed, no controls - a property of the system, not a rule.
  default: {
    display: 'grid',
    gridTemplateColumns: COLUMNS,
    alignItems: 'center',
    height: '34px',
    marginTop: '6px',
    borderRadius: '6px',
    border: `1px dashed ${vt.fg4}`,
    backgroundColor: vt.tintNeutral,
    opacity: 0.85,
    paddingInlineEnd: '8px',
  },
  lock: { color: vt.fg4, display: 'flex', justifyContent: 'center' },
  everything: { gridColumn: '4 / 5', whiteSpace: 'nowrap', fontSize: '12px', fontWeight: tokens.fontWeightSemibold, color: vt.fg2, paddingInlineStart: '8px' },
  defaultNote: { gridColumn: '5 / 8', whiteSpace: 'nowrap', paddingInlineEnd: '8px', fontSize: '11px', color: vt.fg4, textAlign: 'end' },
});

function Chip({ value, enabled }: { value: string; enabled: boolean }) {
  const s = useStyles();
  return (
    <span className={mergeClasses(s.chip, value !== 'any' && s.chipMono, !enabled && s.off)} title={value}>
      {formatEndpoint(value)}
    </span>
  );
}

interface Props {
  rules: PolicyRule[];
  diff: PolicyDiff | null;
  selected: number | null;
  onSelect: (index: number) => void;
  /** Drag and drop: move the rule at `from` to position `to` (0-based). */
  onMove: (from: number, to: number) => void;
  disabled?: boolean;
  /** GET /policy/status: the relay's hit counters; null until loaded or when it failed. */
  status: PolicyStatus | null;
}

/** The Hits 24 h cell's contents: the count, and the last match below it. */
function HitStack({ h }: { h: RuleHits }) {
  const s = useStyles();
  return (
    <span className={s.hitStack}>
      <span className={s.hits}>{h.hits}</span>
      <span className={s.ago} title={lastMatchTitle(h)}>
        {h.lastMatch}
      </span>
    </span>
  );
}

/** Mockup 05's rule table, Meraki's column order, with the relay's hit counters from GET /policy/status. */
export function RuleTable({ rules, diff, selected, onSelect, onMove, disabled, status }: Props) {
  const s = useStyles();
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);
  const heads = ['#', 'Policy', 'Protocol', 'Source', 'Destination', 'Dst port', 'Comment', 'Hits 24 h'];

  const drop = (e: DragEvent, to: number) => {
    e.preventDefault();
    if (dragFrom !== null && dragFrom !== to) onMove(dragFrom, to);
    setDragFrom(null);
    setDropAt(null);
  };

  return (
    <div>
      <Table className={s.table} size="small" aria-label="Firewall rules">
        <TableHeader>
          <TableRow className={s.head}>
            {heads.map((h, i) => (
              <TableHeaderCell key={h} className={mergeClasses(s.cell, i >= 7 && s.end)} style={{ width: WIDTHS[i] }}>
                {h}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rules.map((r, i) => {
            const change = rowChange(r.id, diff);
            const edited = change !== 'none';
            const isSelected = selected === i;
            return (
              <TableRow
                key={r.id || `new-${i}`}
                data-rule={i + 1}
                data-change={change}
                aria-selected={isSelected}
                tabIndex={0}
                draggable={!disabled}
                onClick={() => onSelect(i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(i);
                  }
                }}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  setDragFrom(i);
                }}
                onDragOver={(e) => {
                  if (dragFrom === null) return;
                  e.preventDefault();
                  setDropAt(i);
                }}
                onDragLeave={() => setDropAt(null)}
                onDrop={(e) => drop(e, i)}
                onDragEnd={() => {
                  setDragFrom(null);
                  setDropAt(null);
                }}
                className={mergeClasses(
                  s.row,
                  edited && s.edited,
                  isSelected && s.selected,
                  isSelected && edited && s.selectedEdited,
                  dropAt === i && dragFrom !== i && s.dropTarget,
                  !r.enabled && s.dim,
                )}
              >
                <TableCell className={mergeClasses(s.cell, s.num)}>{i + 1}</TableCell>
                <TableCell className={s.cell}>
                  {/* A disabled rule keeps its pill: the row still says what it WOULD do. */}
                  <span className={s.policy}>
                    <StatePill state={r.action} />
                    {!r.enabled && <span className={mergeClasses(s.chip, s.off)}>off</span>}
                  </span>
                </TableCell>
                <TableCell className={mergeClasses(s.cell, s.mono)}>{formatProtocol(r.protocol)}</TableCell>
                <TableCell className={s.cell}>
                  <Chip value={r.source} enabled={r.enabled} />
                </TableCell>
                <TableCell className={s.cell}>
                  <Chip value={r.destination} enabled={r.enabled} />
                </TableCell>
                <TableCell className={mergeClasses(s.cell, s.mono)}>{formatPorts(r.destinationPorts)}</TableCell>
                <TableCell className={s.cell}>
                  <span className={s.comment} title={r.comment}>
                    {r.comment}
                  </span>
                </TableCell>
                <TableCell className={mergeClasses(s.cell, s.end)} data-col="hits">
                  <HitStack h={ruleHits(status, r.id)} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <div className={s.default} role="group" aria-label="Default rule: deny everything else" data-rule="default">
        <span className={s.lock}>
          <LockClosed12Regular aria-label="Locked" />
        </span>
        <span>
          <StatePill state="deny" />
        </span>
        <span />
        <span className={s.everything}>everything else</span>
        <span className={s.defaultNote}>default policy · cannot be removed</span>
        <span className={s.end} data-col="hits">
          <HitStack h={ruleHits(status, DEFAULT_RULE_ID)} />
        </span>
      </div>
    </div>
  );
}
